// src/utils/addressLookup.js
// Address -> area info (schools + utility providers) autofill adapter.
//
// ─────────────────────────────────────────────────────────────────────────────
// WHAT TO PLUG IN (this is a NO-OP stub until you do all three):
//   1. Pick a provider that maps a street address to school-district / utility
//      data (e.g. a schools API + a utility-territory API, or a single
//      area-data aggregator). Anything that takes an address string and returns
//      the fields below.
//   2. Put the provider's key in an env var named REACT_APP_AREA_INFO_KEY
//      (CRA only exposes vars prefixed REACT_APP_ to the browser). Add it to
//      .env / .env.local — do NOT hard-code the key here.
//   3. Fill in the PROVIDER adapter's `fetchAreaInfo` below: build the request
//      URL(s), send the key, and map the response onto the AREA_INFO_FIELDS
//      keys. Return only the fields you have; omit the rest.
//
// Contract (must hold no matter what):
//   - Returns a plain object with any subset of AREA_INFO_FIELDS keys.
//   - NEVER throws — on missing key, network error, or bad response it resolves
//     to {} so the portal keeps working with no autofill.
//   - The portal only fills fields the client hasn't already typed, so a partial
//     or empty result is always safe.
// ─────────────────────────────────────────────────────────────────────────────

// The exact keys this adapter may return. These line up 1:1 with the Utility
// Information Sheet field keys in portalForms.js so the portal can drop them in
// without any remapping.
export const AREA_INFO_FIELDS = [
  "schoolDistrict",
  "elementary",
  "middle",
  "high",
  "electricCompany",
  "gasCompany",
  "waterCompany",
];

// The env key that gates the whole thing. Absent => stub mode (return {}).
const PROVIDER_KEY = process.env.REACT_APP_AREA_INFO_KEY || "";

// --- PROVIDER adapter -------------------------------------------------------
// NO-OP until a provider is wired up. When you implement it, this is the only
// function you should need to touch. Keep it defensive: any failure must
// resolve to {} (never throw, never reject).
async function fetchAreaInfo(/* address, key */) {
  // TODO(provider): call the school/utility provider(s) with `key`, then map
  // the response onto AREA_INFO_FIELDS. Until then, no provider => no data.
  return {};
}

// Keep only recognized, non-empty string fields from a provider response.
function sanitize(raw) {
  const out = {};
  if (!raw || typeof raw !== "object") return out;
  for (const k of AREA_INFO_FIELDS) {
    const v = raw[k];
    if (typeof v === "string" && v.trim() !== "") out[k] = v.trim();
  }
  return out;
}

/**
 * Look up area info (school district, schools, utility companies) for a street
 * address. Returns a subset of AREA_INFO_FIELDS. Resolves to {} — never throws —
 * when there's no address, no configured provider key, or any error.
 *
 * @param {string} address
 * @returns {Promise<{schoolDistrict?:string, elementary?:string, middle?:string,
 *   high?:string, electricCompany?:string, gasCompany?:string, waterCompany?:string}>}
 */
export async function lookupAreaInfo(address) {
  const addr = String(address || "").trim();
  if (!addr) return {};
  if (!PROVIDER_KEY) return {}; // stub mode — no provider configured yet
  try {
    const raw = await fetchAreaInfo(addr, PROVIDER_KEY);
    return sanitize(raw);
  } catch {
    return {}; // never let a lookup failure break the form
  }
}
